import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useRouteMatch, Link } from "react-router-dom";
import useTimeout from "hooks/useTimeout";
import moment from "moment";
import { makeStyles, Theme, createStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";

import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClock,
  faThermometerHalf,
  faTint,
  faCog,
} from "@fortawesome/free-solid-svg-icons";

import {
  getProbeDataThunk,
  getProbe,
  isLoading,
  getSelectedProbeDataGraphType,
  updateSelectedProbeDataGraphType,
  getProbeSummaryThunk,
} from "features/probe/probeSlice";
import { RootState } from "../../app/rootReducer";
import Graph from "./components/Graph";
import {
  averageTemperature,
  averageHumidity,
  isTooHot,
  isTooCold,
  isTooDry,
  isTooWet,
} from "../../utils/probeData";
import { ProbeDataGraphType } from "../../types/enums";
import Loading from "components/Loading";
import { hubData } from "features/hub/hubSlice";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      paddingLeft: 20,
      paddingRight: 20,
    },
    banner: {
      border: "1px solid red",
    },
    bannerImage: {
      width: "100%",
      height: "auto",
      maxHeight: 300,
    },
    image: {
      width: "100%",
      height: "auto",
    },
  })
);

const Probe = () => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const route = useRouteMatch();

  /**
   * Selectors
   */
  const selectedProbe = useSelector((state: RootState) =>
    getProbe(state, Number(route.params.id))
  );

  const selectedProbeDataGraphType: ProbeDataGraphType = useSelector(
    getSelectedProbeDataGraphType
  );

  const hub = useSelector(hubData);

  /**
   * States
   */
  const [probe, setProbe] = useState<common.Probe>();
  const [lastUpdated, setLastUpdated] = useState<string>();

  /**
   * Life cycles
   */
  // Set state probe data when selector changes
  useEffect(() => {
    if (selectedProbe === undefined && hub?.probeIds)
      dispatch(getProbeSummaryThunk(hub.probeIds));
  }, [hub?.id]);

  useEffect(() => {
    setProbe(selectedProbe);
  }, [selectedProbe]);

  useEffect(() => {
    if (selectedProbe !== undefined) {
      dispatch(getProbeDataThunk(selectedProbe.id, selectedProbeDataGraphType));
    }
  }, [selectedProbeDataGraphType, selectedProbe?.id]);

  const tooHot = probe ? isTooHot(probe) : false;
  const tooCold = probe ? isTooCold(probe) : false;
  const tooDry = probe ? isTooDry(probe) : false;
  const tooWet = probe ? isTooWet(probe) : false;
  const hasProbeData = probe?.probeData?.length > 0;

  const overViewData = () => {
    return (
      <span style={{ flex: "1", paddingLeft: 10 }}>
        <h3 style={{ marginTop: 0, marginBottom: 0 }}>Last Updated</h3>
        <span style={{ color: "#888", fontSize: 18 }}>
          <FontAwesomeIcon icon={faClock} /> {lastUpdated}
        </span>

        <h3 style={{ marginTop: 30, marginBottom: 0 }}>Latest</h3>
        <div style={{ display: "flex", width: 200 }}>
          <span style={{ flex: "1" }}>
            <FontAwesomeIcon
              title={tooHot ? "Too Hot" : tooCold ? "Too Cold" : ""}
              color={tooHot ? "red" : tooCold ? "blue" : "black"}
              icon={faThermometerHalf}
            />{" "}
            {probe.latestTemperature}
          </span>

          {probe.hasHumidity && (
            <span style={{ flex: "1" }}>
              <FontAwesomeIcon
                title={tooDry ? "Too Dry" : tooWet ? "Too Wet" : ""}
                color={tooDry ? "red" : tooWet ? "blue" : "black"}
                icon={faTint}
              />{" "}
              {probe.latestHumidity}
            </span>
          )}
        </div>

        <h3 style={{ marginTop: 30, marginBottom: 0 }}>Average</h3>
        <div style={{ display: "flex", width: 200 }}>
          <span style={{ flex: "1" }}>
            <FontAwesomeIcon
              title={tooHot ? "Too Hot" : tooCold ? "Too Cold" : ""}
              color={tooHot ? "red" : tooCold ? "blue" : "black"}
              icon={faThermometerHalf}
            />{" "}
            {hasProbeData
              ? averageTemperature(probe.probeData)
              : "No available data"}
          </span>

          {probe.hasHumidity && (
            <span style={{ flex: "1" }}>
              <FontAwesomeIcon
                title={tooDry ? "Too Dry" : tooWet ? "Too Wet" : ""}
                color={tooDry ? "red" : tooWet ? "blue" : "black"}
                icon={faTint}
              />{" "}
              {hasProbeData
                ? averageHumidity(probe.probeData)
                : "No available data"}
            </span>
          )}
        </div>
      </span>
    );
  };

  const probeSettingData = () => {
    return (
      <span style={{ flex: "1", paddingLeft: 10 }}>
        <h3 style={{ marginTop: 0, marginBottom: 0 }}>Probe Settings</h3>

        <span>
          <b>Minimum Temperature:</b> {probe.minimumTemperature}
          <br />
          <b>Maximum Temperature:</b> {probe.maximumTemperature}
          <br />
          <b>Minimum Humidity:</b> {probe.minimumHumidity}
          <br />
          <b>Maximum Humidity:</b> {probe.maximumHumidity}
        </span>
      </span>
    );
  };

  return probe && probe.probeData ? (
    <React.Fragment>
      <Container className={classes.root}>
        <div style={{ display: "flex" }}>
          <span style={{ flex: 1, justifyContent: "flex-start" }}>
            <h1>{probe.name}</h1>
          </span>

          <span style={{ flex: 1, justifyContent: "flex-end" }}>
            <span style={{ float: "right", paddingTop: 25 }}>
              <Link to={`/probeSettings/${probe.id}`}>
                <FontAwesomeIcon style={{ fontSize: 24 }} icon={faCog} />
              </Link>
            </span>
          </span>
        </div>

        <div style={{ display: "flex" }}>
          <span style={{ flex: "1", maxWidth: "40%" }}>
            <img
              src={`data:image/jpeg;base64,${probe.image}`}
              alt={probe.name}
              className={classes.image}
            />
          </span>

          {overViewData()}
          {/* {probeSettingData()} */}
        </div>

        <br />
        <br />
        <br />

        <Select
          labelId="demo-simple-select-helper-label"
          id="demo-simple-select-helper"
          value={selectedProbeDataGraphType}
          label="Probe Data Filter Options"
          onChange={(selection) =>
            dispatch(
              updateSelectedProbeDataGraphType(
                selection.target.value as ProbeDataGraphType
              )
            )
          }
        >
          <MenuItem value={ProbeDataGraphType.last24HoursAverage}>
            Last 24 Hours (Average)
          </MenuItem>
          <MenuItem value={ProbeDataGraphType.last7DaysAverage}>
            Last 7 Days (Average)
          </MenuItem>
          <MenuItem value={ProbeDataGraphType.last24HoursMax}>
            Last 24 Hours (Max)
          </MenuItem>
          <MenuItem value={ProbeDataGraphType.last7DaysMax}>
            Last 7 Days (Max)
          </MenuItem>
          <MenuItem value={ProbeDataGraphType.last24HoursMin}>
            Last 24 Hours (Min)
          </MenuItem>
          <MenuItem value={ProbeDataGraphType.last7DaysMin}>
            Last 7 Days (Min)
          </MenuItem>
        </Select>

        <br />
        <br />

        {hasProbeData ? (
          <Graph probeData={probe.probeData} />
        ) : (
          <div>
            No available data found. Please try selected a different filter
          </div>
        )}
      </Container>
    </React.Fragment>
  ) : (
    <div>
      <Loading></Loading>
    </div>
  );
};

export default Probe;
